import { type FormEvent, useEffect, useState } from 'react';
import { useDialogs } from '../../stores/dialogs';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';

/** Renders the confirm / prompt dialogs requested through stores/dialogs. */
export function DialogHost() {
  const pending = useDialogs((s) => s.pending);
  const [value, setValue] = useState('');

  useEffect(() => {
    if (pending?.kind === 'prompt') setValue(pending.request.initialValue ?? '');
  }, [pending]);

  if (!pending) return null;

  const close = (result: boolean) => {
    useDialogs.setState({ pending: null });
    if (pending.kind === 'confirm') pending.resolve(result);
    else pending.resolve(result ? value.trim() : null);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (pending.kind === 'prompt' && !value.trim()) return;
    close(true);
  };

  const { request } = pending;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && close(false)}
      title={request.title}
      description={pending.kind === 'confirm' ? pending.request.message : undefined}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        {pending.kind === 'prompt' && (
          <Input
            autoFocus
            label={pending.request.label}
            placeholder={pending.request.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            maxLength={120}
          />
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            autoFocus={pending.kind === 'confirm'}
            variant={
              pending.kind === 'confirm' && pending.request.tone === 'danger' ? 'danger' : 'primary'
            }
          >
            {request.confirmLabel ?? 'OK'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
